import type { ApiClient } from '@carbonwebmail/api-client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { CampaignReportPage } from '../pages/campaigns/CampaignReportPage.js'
import { CampaignsPage } from '../pages/campaigns/CampaignsPage.js'
import { ComposerPage } from '../pages/campaigns/ComposerPage.js'
import { NewCampaignPage } from '../pages/campaigns/NewCampaignPage.js'
import { SendReviewPage } from '../pages/campaigns/SendReviewPage.js'
import { ComposePage } from '../pages/compose/ComposePage.js'
import { ContactDetailPage } from '../pages/contacts/ContactDetailPage.js'
import { ContactsBoard } from '../pages/contacts/ContactsBoard.js'
import { ContactsList } from '../pages/contacts/ContactsList.js'
import { DashboardPage } from '../pages/dashboard/DashboardPage.js'
import { ConsentPage } from '../pages/deliverability/ConsentPage.js'
import { ReputationPage } from '../pages/deliverability/ReputationPage.js'
import { SendingDomainsPage } from '../pages/deliverability/SendingDomainsPage.js'
import { SendsPage } from '../pages/deliverability/SendsPage.js'
import { SuppressionsPage } from '../pages/deliverability/SuppressionsPage.js'
import { DesignerPage } from '../pages/designer/DesignerPage.js'
import { ImportPage } from '../pages/import/ImportPage.js'
import { JoinPage } from '../pages/join/JoinPage.js'
import { PrivacyPage } from '../pages/legal/PrivacyPage.js'
import { TermsPage } from '../pages/legal/TermsPage.js'
import { MondayPage } from '../pages/monday/MondayPage.js'
import { NotFoundPage } from '../pages/NotFoundPage.js'
import { OnboardingPage } from '../pages/onboarding/OnboardingPage.js'
import { PreviewPage } from '../pages/preview/PreviewPage.js'
import { SegmentBuilderPage } from '../pages/segments/SegmentBuilderPage.js'
import { SegmentMembersPage } from '../pages/segments/SegmentMembersPage.js'
import { SegmentsPage } from '../pages/segments/SegmentsPage.js'
import { ApiKeysPage } from '../pages/settings/ApiKeysPage.js'
import { EmailProviderPage } from '../pages/settings/EmailProviderPage.js'
import { PlanPage } from '../pages/settings/PlanPage.js'
import { ProfilePage } from '../pages/settings/ProfilePage.js'
import { SettingsBoard } from '../pages/settings/SettingsBoard.js'
import { SettingsPage } from '../pages/settings/SettingsPage.js'
import { TeamPage } from '../pages/settings/TeamPage.js'
import { SignInPage } from '../pages/signin/SignInPage.js'
import { SignUpPage } from '../pages/signup/SignUpPage.js'
import { SupportPage } from '../pages/support/SupportPage.js'
import { TemplatesPage } from '../pages/templates/TemplatesPage.js'
import { UnsubscribePage } from '../pages/unsubscribe/UnsubscribePage.js'
import { AppShell } from '../ui/AppShell.js'
import { useTheme } from '../ui/useTheme.js'
import { useSession } from './useSession.js'
import { useShellStatus } from './useShellStatus.js'

/**
 * Routes are added as screens are built, never ahead of them.
 *
 * A navigation full of links to screens that do not exist yet is the same
 * problem as a settings toggle that writes a column nothing reads: the
 * interface promises something the system does not do.
 *
 * The split between public and signed-in routes is the important structure
 * here. Unsubscribe and the auth screens are reachable without a session by
 * necessity; everything else is inside the shell, and the shell does not render
 * until the server has said who is asking.
 *
 * **There are now four session states rather than three.** Somebody can be
 * signed in with the identity provider and belong to no organisation here —
 * `needs-organisation` — which is an ordinary state after creating an identity
 * and a dead end until they name one. It gets the whole screen for the same
 * reason the sign-in screen does: there is nothing else they can usefully do.
 */
export function App({ client }: { readonly client: ApiClient }) {
  const { state, refresh, signOut } = useSession(client)
  const { theme, toggle } = useTheme()
  // Asked for once the session is known, so a signed-out visitor does not send
  // an authenticated request that can only 401.
  const status = useShellStatus(client, state.status === 'signed-in')

  return (
    <BrowserRouter>
      <Routes>
        {/* Public. Arrived at from a link in an email, or by someone with no
            account yet. */}
        <Route path="/unsubscribe/:token" element={<UnsubscribePage client={client} />} />
        {/*
          Public for the same reason the unsubscribe page is: a recipient
          deciding whether to trust a sender has no account here and never
          will. Google's OAuth review additionally requires the privacy notice
          on a URL that does not ask for a login before it will grant the Gmail
          scope this product sends with.
        */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        {/*
          Wildcards, because the identity provider's components route their own
          steps — a second factor, a reset, an OAuth return — as real addresses
          underneath these. A single exact path would 404 on every step after
          the first.
        */}
        <Route path="/signup/*" element={<SignUpPage />} />
        <Route path="/signin/*" element={<SignInPage />} />
        {/* The preview is public by necessity: the page has to say which
            organisation the link is for before anybody signs in. Accepting is
            not — the server requires an identity for that. */}
        <Route path="/join/:token" element={<JoinPage client={client} onJoined={refresh} />} />

        {/*
          Compose is full-screen and deliberately outside `AppShell`. Laying
          out an email needs the width — the sidebar, the search bar and a page
          heading all compete with the thing being made — and its own toolbar is
          the only thing that can ask about unsaved work on the way out.

          Behind the session like everything else: `state.status` is checked
          here rather than by the shell, because there is no shell.
        */}
        {state.status === 'signed-in' ? (
          <Route path="/compose/:kind/:id" element={<ComposePage client={client} />} />
        ) : null}

        {/*
          The preview is a tab of its own, for the same reason and with less to
          it: the question is how the message will arrive, and the shell around
          it is not part of the answer.
        */}
        {state.status === 'signed-in' ? (
          <Route path="/preview" element={<PreviewPage client={client} />} />
        ) : null}

        {state.status === 'loading' ? (
          // Nothing is decided yet. Rendering the sign-in screen here would
          // bounce a signed-in person to a login form for a moment on every
          // refresh, which reads as having been logged out.
          <Route path="*" element={<LoadingScreen />} />
        ) : null}

        {state.status === 'signed-out' ? (
          <Route path="*" element={<Navigate replace to="/signin" />} />
        ) : null}

        {/*
          Signed in with the provider, belonging to nothing here. The whole
          screen, because there is nothing else they can do until there is an
          organisation — and `refresh` rather than a redirect, since the app
          learns it has one only by asking the server again.
        */}
        {state.status === 'needs-organisation' ? (
          <Route path="*" element={<OnboardingPage client={client} onCreated={refresh} />} />
        ) : null}

        {state.status === 'signed-in' ? (
          <Route
            path="*"
            element={
              <AppShell
                client={client}
                organizationName={state.session.organizationName}
                email={state.session.email}
                yourRole={state.session.role}
                theme={theme}
                onToggleTheme={toggle}
                unknownSends={status.unknownSends}
                bounceWarning={status.bounceWarning}
                onSignOut={() => void signOut()}
              >
                <Routes>
                  <Route path="/" element={<Navigate replace to="/dashboard" />} />
                  <Route path="/dashboard" element={<DashboardPage client={client} />} />
                  {/*
                    One board for the audience, with its panels inside it. They
                    were five sidebar entries and five screens for one subject,
                    which put the thing somebody reaches for next behind a
                    navigation step.

                    `:contactId` stays a sibling rather than a child: a contact
                    is a screen of its own, not a tab of this one. A static
                    child outranks it, so /contacts/import is the board.
                  */}
                  <Route element={<ContactsBoard client={client} />} path="/contacts">
                    <Route element={<ContactsList client={client} />} index />
                    <Route element={<ImportPage client={client} />} path="import" />
                  </Route>
                  <Route
                    path="/contacts/:contactId"
                    element={<ContactDetailPage client={client} />}
                  />
                  <Route path="/segments" element={<SegmentsPage client={client} />} />
                  {/*
                    No `/segments/new`: making one is a dialog over the board,
                    the way New contact is. A route as well would be a second
                    way to create a segment, and the builder only ever edits.
                  */}
                  <Route
                    path="/segments/:segmentId"
                    element={<SegmentBuilderPage client={client} />}
                  />
                  <Route
                    path="/segments/:segmentId/members"
                    element={<SegmentMembersPage client={client} />}
                  />
                  <Route path="/campaigns" element={<CampaignsPage client={client} />} />
                  <Route path="/campaigns/new" element={<NewCampaignPage client={client} />} />
                  <Route
                    path="/campaigns/:campaignId"
                    element={<CampaignReportPage client={client} />}
                  />
                  <Route
                    path="/campaigns/:campaignId/edit"
                    element={<ComposerPage client={client} />}
                  />
                  <Route
                    path="/campaigns/:campaignId/send"
                    element={<SendReviewPage client={client} />}
                  />
                  <Route path="/templates" element={<TemplatesPage client={client} />} />
                  <Route path="/designer" element={<DesignerPage client={client} />} />
                  <Route path="/sends" element={<SendsPage client={client} />} />
                  <Route path="/suppressions" element={<SuppressionsPage client={client} />} />
                  <Route path="/reputation" element={<ReputationPage client={client} />} />
                  <Route path="/consent" element={<ConsentPage client={client} />} />
                  <Route path="/support" element={<SupportPage client={client} />} />
                  {/*
                    One board, six tabs. Each was a route and a sidebar entry of
                    its own; they are one subject, and `SettingsBoard` says why.
                    The profile is the index rather than a redirect, so the
                    first tab is an address somebody can go back to.
                  */}
                  <Route element={<SettingsBoard />} path="/settings">
                    <Route index element={<ProfilePage client={client} />} />
                    <Route path="organisation" element={<SettingsPage client={client} />} />
                    <Route path="team" element={<TeamPage client={client} />} />
                    <Route path="plan" element={<PlanPage client={client} />} />
                    <Route path="provider" element={<EmailProviderPage client={client} />} />
                    <Route path="domains" element={<SendingDomainsPage client={client} />} />
                    <Route path="api-keys" element={<ApiKeysPage client={client} />} />
                    <Route path="monday" element={<MondayPage client={client} />} />
                  </Route>
                  {/*
                    Mounted for everyone, and refused by the server for anyone
                    who is not an operator. A route that existed only when a
                    browser-held flag said so would be an authorisation decision
                    made in the browser.
                  */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </AppShell>
            }
          />
        ) : null}
      </Routes>
    </BrowserRouter>
  )
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <p className="text-[13px] text-t3">Loading…</p>
    </div>
  )
}
